/**
 * Shared building blocks for the Settings screens — literal ports of the
 * styles the mobile settings stack repeats (app/(tabs)/settings.tsx and
 * app/settings/*.tsx). Numbers are copied from those StyleSheets.
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { Ionicon, type IoniconName } from '@/components/ui/Ionicon'

// ─── Icons ───────────────────────────────────────────────────────────────────

/** Mobile DirectionalIcon: chevrons / arrows mirror in RTL. */
export function DirectionalIcon({
  name,
  size,
  color,
  className,
}: {
  name: 'chevron-forward' | 'chevron-back' | 'arrow-back'
  size: number
  color?: string
  className?: string
}) {
  return <Ionicon name={name} size={size} color={color} className={clsx('rtl:-scale-x-100', className)} />
}

/** Row chevron — chevron-forward 20 in the border colour. */
export function RowChevron({ color = 'var(--color-border)', size = 20 }: { color?: string; size?: number }) {
  return <DirectionalIcon name="chevron-forward" size={size} color={color} />
}

// ─── Main settings list (app/(tabs)/settings.tsx) ────────────────────────────

/** `section` (mb xl) + `sectionTitle` (h4 size, semiBold, mb md) + Card (radius xl, Shadows.md, padding 0). */
export function SettingsSection({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <section className="mb-xl">
      <h2 className="mb-md text-[18px] font-semibold leading-[normal] text-text">{title}</h2>
      {subtitle ? (
        // sectionSubtitle: caption, opacity .6, marginTop -xs, marginBottom md
        <p className="-mt-xs mb-md text-[12px] leading-[normal] text-text opacity-60">{subtitle}</p>
      ) : null}
      <div className="divide-y divide-border overflow-hidden rounded-xl bg-card shadow-md">{children}</div>
      {footer}
    </section>
  )
}

/**
 * `settingRow`: row · space-between · center · padding md. Label body/medium,
 * description caption/regular at opacity .7 (text colour), marginTop 2.
 */
export function SettingsRow({
  label,
  description,
  leading,
  badge,
  trailing,
  danger,
  onClick,
  descriptionLines,
}: {
  label: string
  description?: ReactNode
  /** Icon before the label (limitLabelRow: row · center · gap 8). */
  leading?: ReactNode
  badge?: ReactNode
  /** Replaces the default chevron. */
  trailing?: ReactNode
  danger?: boolean
  onClick: () => void
  /** numberOfLines={1} on the description. */
  descriptionLines?: 1
}) {
  const labelNode = (
    <span
      className={clsx(
        'block text-body font-medium leading-[normal]',
        danger ? 'text-trigger' : 'text-text',
      )}
    >
      {label}
    </span>
  )
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between p-md text-start transition-opacity duration-150 hover:bg-text/[0.03] active:opacity-20"
    >
      <span className="me-md min-w-0 flex-1">
        {leading || badge ? (
          <span className="flex items-center gap-sm">
            {leading}
            {labelNode}
            {badge}
          </span>
        ) : (
          labelNode
        )}
        {description != null ? (
          <span
            className={clsx(
              'mt-[2px] block text-[12px] leading-[normal] text-text opacity-70',
              descriptionLines === 1 && 'truncate',
            )}
          >
            {description}
          </span>
        ) : null}
      </span>
      {trailing ?? <RowChevron color={danger ? 'var(--color-trigger)' : undefined} />}
    </button>
  )
}

// ─── Sub-screen header (app/settings/*.tsx) ──────────────────────────────────

/**
 * Draft state seeded from the profile. Mobile screens mount with the profile
 * already loaded; on a cold web deep link it can arrive a moment later, so
 * the draft is (re)seeded once when the profile first shows up.
 */
export function useProfileDraft<T>(ready: boolean, initial: () => T) {
  const [value, setValue] = useState<T>(initial)
  const seeded = useRef(ready)
  useEffect(() => {
    if (ready && !seeded.current) {
      seeded.current = true
      setValue(initial())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])
  return [value, setValue] as const
}

/** Go back like router.back(), falling back to a route on a cold deep link. */
export function useGoBack(fallback = '/settings') {
  const navigate = useNavigate()
  return () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0
    if (idx > 0) navigate(-1)
    else navigate(fallback, { replace: true })
  }
}

/**
 * Sub-screen header.
 * - `bordered` (liver-status, health-conditions, diet-preferences,
 *   optional-nutrients, fib4-calculator): paddingHorizontal sm, paddingBottom md,
 *   borderBottom 1; 44×44 back with chevron-back 28; title h3/semiBold; right
 *   Save (padding md/sm, body/semiBold, primary when enabled else border colour).
 * - `plain` (change-password, sources): paddingHorizontal md, 40×40 back, title 18.
 * Stays pinned like the mobile header (content scrolls under it).
 */
export function SubScreenHeader({
  title,
  onBack,
  variant = 'bordered',
  save,
}: {
  title: string
  onBack: () => void
  variant?: 'bordered' | 'plain'
  save?: { label: string; enabled: boolean; onClick: () => void }
}) {
  const { t } = useTranslation()
  const bordered = variant === 'bordered'
  const btn = bordered ? 'h-[44px] w-[44px]' : 'h-[40px] w-[40px]'
  return (
    <header
      className={clsx(
        'sticky top-0 z-20 flex items-center justify-between bg-bg pb-md pt-sm',
        bordered ? 'border-b border-border px-sm' : 'px-md',
      )}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label={t('common.back', 'Back')}
        className={clsx(btn, 'flex items-center justify-center text-text transition-opacity active:opacity-20')}
      >
        <DirectionalIcon name="chevron-back" size={28} />
      </button>
      <h1
        className={clsx(
          'min-w-0 truncate text-center font-semibold leading-[normal] text-text',
          bordered ? 'text-[20px]' : 'text-[18px]',
        )}
      >
        {title}
      </h1>
      {save ? (
        <button
          type="button"
          onClick={save.onClick}
          disabled={!save.enabled}
          className="px-md py-sm text-body font-semibold leading-[normal] transition-opacity active:opacity-20"
          style={{ color: save.enabled ? 'var(--color-primary)' : 'var(--color-border)' }}
        >
          {save.label}
        </button>
      ) : (
        <span className={bordered ? 'w-[44px]' : 'w-[40px]'} aria-hidden />
      )}
    </header>
  )
}

// ─── Controls ────────────────────────────────────────────────────────────────

/** RN iOS Switch (51×31, white 27px thumb); track primary when on, border colour when off. */
export function Switch({
  value,
  onChange,
  disabled,
  label,
}: {
  value: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  label?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!value)}
      className="relative h-[31px] w-[51px] shrink-0 rounded-full transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50"
      style={{ backgroundColor: value ? 'var(--color-primary)' : 'var(--color-border)' }}
    >
      <span
        className="absolute top-[2px] h-[27px] w-[27px] rounded-full transition-all duration-200"
        style={{
          backgroundColor: 'var(--color-button-primary-text)',
          boxShadow: '0 3px 8px rgba(0,0,0,0.15), 0 3px 1px rgba(0,0,0,0.06)',
          insetInlineStart: value ? 22 : 2,
        }}
      />
    </button>
  )
}

/** radioOuter 24 (border 2) / radioInner 12, marginLeft sm. */
export function Radio({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className="ms-sm flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border-2"
      style={{ borderColor: selected ? 'var(--color-primary)' : 'var(--color-border)' }}
    >
      {selected ? <span className="h-[12px] w-[12px] rounded-full bg-primary" /> : null}
    </span>
  )
}

/** checkbox 24 (or 22) · radius 6 · border 2 · white checkmark when checked. */
export function Checkbox({ checked, size = 24, iconSize = 16 }: { checked: boolean; size?: number; iconSize?: number }) {
  return (
    <span
      aria-hidden
      className="ms-sm flex shrink-0 items-center justify-center rounded-[6px] border-2"
      style={{
        width: size,
        height: size,
        backgroundColor: checked ? 'var(--color-primary)' : 'transparent',
        borderColor: checked ? 'var(--color-primary)' : 'var(--color-border)',
      }}
    >
      {checked ? <Ionicon name="checkmark" size={iconSize} color="var(--color-button-primary-text)" /> : null}
    </span>
  )
}

/**
 * Selectable option card (optionCard): row · space-between · padding md ·
 * radius lg · border 1; selected = primary@15 fill + primary border.
 */
export function OptionCard({
  selected,
  onClick,
  glyph,
  label,
  labelExtra,
  description,
  control,
  className,
  style,
}: {
  selected: boolean
  onClick: () => void
  glyph: ReactNode
  label: string
  labelExtra?: ReactNode
  description?: string
  control: ReactNode
  className?: string
  style?: CSSProperties
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        'flex w-full items-center justify-between rounded-lg border p-md text-start transition-opacity active:opacity-70',
        selected ? 'border-primary bg-primary/[0.082]' : 'border-border bg-card',
        className,
      )}
      style={style}
    >
      <span className="flex min-w-0 flex-1 items-center gap-md">
        {glyph}
        <span className="min-w-0 flex-1">
          {labelExtra ? (
            <span className="mb-[2px] flex flex-wrap items-center gap-sm">
              <span className="text-body font-semibold leading-[normal] text-text">{label}</span>
              {labelExtra}
            </span>
          ) : (
            <span className={clsx('block text-body font-semibold leading-[normal] text-text', description && 'mb-[2px]')}>
              {label}
            </span>
          )}
          {description ? (
            <span className="block text-[12px] leading-[16px] text-text-secondary">{description}</span>
          ) : null}
        </span>
      </span>
      {control}
    </button>
  )
}

// ─── Modals ──────────────────────────────────────────────────────────────────

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
}

/**
 * Mobile `<Modal presentationStyle="pageSheet">`: a near-full-height sheet on
 * phone widths, a centered card (≤ 600px) on desktop. Header = close (28) ·
 * title (h3 / semiBold) · right action, padding lg, borderBottom 1; the sheet
 * body is the screen background colour.
 */
export function PageSheet({
  open,
  onClose,
  title,
  titleIcon,
  right,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  titleIcon?: ReactNode
  right?: ReactNode
  children: ReactNode
}) {
  const { t } = useTranslation()
  useEscape(open, onClose)
  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-50 flex animate-fade-in items-end justify-center bg-scrim md:items-center md:p-lg"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="flex h-[calc(100dvh-24px)] w-full max-w-content animate-slide-up flex-col overflow-hidden rounded-t-[12px] bg-bg md:h-auto md:max-h-[85vh] md:rounded-xl md:shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border p-lg">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="text-text transition-opacity active:opacity-20"
          >
            <Ionicon name="close" size={28} />
          </button>
          <div className="mx-sm flex min-w-0 flex-1 items-center justify-center gap-sm">
            {titleIcon}
            <h2 className="min-w-0 text-center text-[20px] font-semibold leading-[normal] text-text">{title}</h2>
          </div>
          {right ?? <span className="w-[28px]" aria-hidden />}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  )
}

/** Header "Save" text button used in page sheets (saveButton: body / semiBold, primary). */
export function SheetSaveButton({
  label,
  onClick,
  disabled,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-body font-semibold leading-[normal] text-primary transition-opacity active:opacity-20 disabled:opacity-40"
    >
      {label}
    </button>
  )
}

export interface AlertButton {
  text: string
  style?: 'default' | 'cancel' | 'destructive'
  onPress?: () => void
}

/**
 * React Native `Alert.alert(title, message, buttons)` — rendered as the iOS
 * system alert (270pt, radius 14, hairline-separated buttons).
 */
export function AlertDialog({
  open,
  title,
  message,
  buttons,
  onClose,
}: {
  open: boolean
  title: string
  message?: string
  buttons: AlertButton[]
  onClose: () => void
}) {
  useEscape(open, onClose)
  if (!open) return null
  const row = buttons.length === 2
  return (
    <div
      className="fixed inset-0 z-[60] flex animate-fade-in items-center justify-center bg-black/40 p-lg"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-[270px] overflow-hidden rounded-[14px] bg-bg-elevated text-center shadow-xl">
        <div className="px-md pb-[18px] pt-[19px]">
          <p className="text-[17px] font-semibold leading-[22px] text-text">{title}</p>
          {message ? (
            <p className="mt-[2px] whitespace-pre-line text-[13px] leading-[18px] text-text">{message}</p>
          ) : null}
        </div>
        <div className={clsx('border-t border-border', row ? 'flex' : 'flex flex-col divide-y divide-border')}>
          {buttons.map((b, i) => (
            <button
              key={b.text + i}
              type="button"
              onClick={() => {
                onClose()
                b.onPress?.()
              }}
              className={clsx(
                'h-[44px] flex-1 text-[17px] leading-[22px] transition-colors hover:bg-text/[0.04] active:bg-text/[0.08]',
                row && i > 0 && 'border-s border-border',
                b.style === 'cancel' ? 'font-semibold' : 'font-normal',
              )}
              style={{ color: b.style === 'destructive' ? 'var(--color-error)' : 'var(--color-primary)' }}
            >
              {b.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Shared state shape for one-at-a-time alerts. */
export interface AlertState {
  title: string
  message?: string
  buttons?: AlertButton[]
}

// ─── Toast (react-native-toast-message, type success, position bottom) ──────

const TOAST_KEY = 'liverly.settingsToast'

/** Queue a success toast for the next screen (e.g. after router.back()). */
export function queueSettingsToast(text: string) {
  try {
    sessionStorage.setItem(TOAST_KEY, text)
  } catch {
    // Storage unavailable — skip the toast.
  }
}

/** Shows (once) a toast queued by queueSettingsToast. */
export function useQueuedToast(): string | null {
  const [text, setText] = useState<string | null>(null)
  useEffect(() => {
    try {
      const v = sessionStorage.getItem(TOAST_KEY)
      if (v) {
        sessionStorage.removeItem(TOAST_KEY)
        setText(v)
      }
    } catch {
      // Storage unavailable — nothing queued.
    }
  }, [])
  useEffect(() => {
    if (!text) return
    const id = setTimeout(() => setText(null), 3500)
    return () => clearTimeout(id)
  }, [text])
  return text
}

export function Toast({ text }: { text: string }) {
  return (
    <div
      role="status"
      className="pointer-events-none fixed inset-x-0 bottom-[110px] z-[60] flex justify-center px-md md:bottom-xl"
    >
      <div className="flex max-w-content animate-slide-up items-center gap-sm rounded-lg border-s-4 border-success bg-bg-elevated px-md py-smd shadow-lg">
        <Ionicon name="checkmark-circle" size={20} color="var(--color-success)" />
        <p className="text-[14px] font-medium leading-[normal] text-text">{text}</p>
      </div>
    </div>
  )
}

export type { IoniconName }
